import { FC } from "react";

import Stack from "@mui/material/Stack";
import { RaffleComplianceDisclaimerVariant } from "@simplyk/common";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ExternalLink } from "@/icons/outlined";

export interface RaffleComplianceDisclaimerDialogProps {
  open: boolean;
  variant: RaffleComplianceDisclaimerVariant;
  learnMoreUrl: string;
  isConfirming?: boolean;
  onConfirm: () => void;
  onBack: () => void;
  inline?: boolean;
}

export const RaffleComplianceDisclaimerDialog: FC<RaffleComplianceDisclaimerDialogProps> = ({
  open,
  variant,
  learnMoreUrl,
  isConfirming = false,
  onConfirm,
  onBack,
  inline,
}) => {
  const { t } = useTranslate();
  const isUs = variant === "US";

  return (
    <DashboardDialog open={open} onClose={onBack} vibe="brand" maxWidth="xs" inline={inline}>
      <DashboardDialogBody
        slots={{
          header: (
            <DashboardDialogHeader
              title={t("dashboard", isUs ? "raffleComplianceDisclaimer.titleUS" : "raffleComplianceDisclaimer.titleCA")}
            />
          ),
          content: (
            <DashboardDialogContent>
              <Stack gap={1.5}>
                <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                  {t("dashboard", isUs ? "raffleComplianceDisclaimer.bodyUS" : "raffleComplianceDisclaimer.bodyCA")}
                </DashboardTypography>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <DashboardLink
                    href={learnMoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    vibe="brand"
                    variant="body2"
                  >
                    {t("dashboard", "raffleComplianceDisclaimer.learnMore")}
                  </DashboardLink>
                  <DashboardIcon size="small" vibe="text-brand-moderate">
                    <ExternalLink />
                  </DashboardIcon>
                </Stack>
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton
                type="button"
                onClick={onConfirm}
                disabled={isConfirming}
                data-test="raffle-compliance-disclaimer-confirm"
              >
                {t("dashboard", "raffleComplianceDisclaimer.confirm")}
              </DashboardButton>
              <DashboardButton onClick={onBack} disabled={isConfirming} data-test="raffle-compliance-disclaimer-back">
                {t("dashboard", "raffleComplianceDisclaimer.back")}
              </DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
